import { useQuery } from "@tanstack/react-query";
import { useAuth } from "./useAuth";
import { useEmpresaAtiva } from "./useEmpresaAtiva";
import { apiFetch } from "../lib/api";

/**
 * 🔒 UMA RÉGUA SÓ PRO WHATSAPP — a permissão mora AQUI, e em nenhum outro lugar.
 *
 * O #1560 (11/08) mediu a doença certa: a tela ADIVINHAVA por `papel + empresa`
 * quem podia abrir as conversas, enquanto o servidor decide por LISTA NOMINAL
 * (`site_config.whatsapp_acesso`). Ele consertou o Layout — e o número não se
 * mexeu.
 *
 * 🟥 POR QUE O CONSERTO NÃO CONSERTOU (medido em 14/08, `app_errors`):
 *   · até 31/07 ......... 10,2 negativas por dia ativo
 *   · 01→11/08 (PRÉ) .... 3,2   ← a queda veio da VIAGEM, não do conserto
 *   · pós-deploy ........ 3,3   ← idêntico ao pré. Efeito líquido: ZERO.
 *
 * O Layout ficou certo e o `SinoNotificacoes` continuou chamando `/nao-lidas`
 * com `enabled: ehAdmin && empresaAtiva` — sem perguntar nada. Pior: as duas
 * consultas usavam a MESMA queryKey `["wa-nao-lidas", empresaId]`. No React
 * Query isso é UMA consulta com DOIS observadores, e ela dispara se QUALQUER um
 * estiver habilitado — então trancar um dos dois não tranca coisa nenhuma.
 * Conferido no bundle publicado (`index-DP07NIQD.js`): o Layout já vinha com
 * `podeVer===!0` e o Sino, ao lado, com `enabled:d`.
 *
 * Quem pagou: jose@m5group.com.br, admin da M5 fora da allowlist — 192 negativas
 * de 13/07 a 14/08, TODAS dele. O selo nunca preencheu, e nunca ia preencher.
 *
 * A lição que virou desenho: gate espalhado por chamador não é gate. Enquanto
 * houver dois lugares que sabem montar a chamada, consertar um é teatro. Por
 * isso a chamada crua não existe mais fora deste arquivo — e
 * `tests/guardrail-whatsapp-uma-regua.test.ts` reprova quem trouxer de volta.
 */

/** A pergunta que qualquer um pode fazer: eu posso abrir as conversas? */
export function useWhatsappPodeVer(): boolean {
  const { user, getAccessToken } = useAuth();
  const { ativa: empresaAtiva } = useEmpresaAtiva();
  const role = user?.role;

  // O papel continua aqui só pra NÃO PERGUNTAR quando é obviamente inútil
  // (cliente não tem inbox). Quem DECIDE é o `podeVer` que vem do servidor.
  const podeOperar = role === "admin" || role === "encarregado";

  const status = useQuery({
    queryKey: ["wa-status", empresaAtiva?.id],
    queryFn: () => apiFetch<{ podeVer?: boolean }>("/whatsapp/status", getAccessToken()),
    enabled: podeOperar && !!empresaAtiva?.id,
    staleTime: 5 * 60 * 1000, // permissão não muda de minuto em minuto
  });

  return podeOperar && status.data?.podeVer === true;
}

/**
 * O total de não-lidas — que só é PEDIDO a quem pode ver.
 *
 * Devolve `podeVer` junto porque quem mostra o selo também precisa decidir se
 * mostra o ITEM; separar as duas coisas foi o que criou o problema original.
 */
export function useWhatsappNaoLidas(): { total: number; podeVer: boolean } {
  const { getAccessToken } = useAuth();
  const { ativa: empresaAtiva } = useEmpresaAtiva();
  const podeVer = useWhatsappPodeVer();

  const naoLidas = useQuery({
    queryKey: ["wa-nao-lidas", empresaAtiva?.id],
    queryFn: () => apiFetch<{ total: number }>("/whatsapp/nao-lidas", getAccessToken()),
    enabled: podeVer && !!empresaAtiva?.id,
    staleTime: 10 * 1000,
    refetchInterval: 20 * 1000, // fallback; o Realtime do Layout atualiza na hora
    refetchOnWindowFocus: true,
  });

  return { total: naoLidas.data?.total ?? 0, podeVer };
}
